import { CheckCircle2, Info, Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * `pending` — work is still in flight (a poll is running).
 * `notice` — it didn't land as expected: a poll timed out, or something was cancelled.
 * `success` — it landed.
 */
export type BannerState = 'pending' | 'notice' | 'success';

interface StatusBannerProps {
  state: BannerState;
  title: string;
  description: ReactNode;
  /** Rendered between title and description — e.g. the discount callout. */
  children?: ReactNode;
}

export function StatusBanner({ state, title, description, children }: StatusBannerProps) {
  return (
    <div className={`mb-4 border-b px-4 py-2 ${state === 'notice' ? 'highlight-amber' : 'highlight-green'}`}>
      <div className="max-w-5xl w-full mx-auto">
        <div className="flex items-start gap-2">
          {state === 'pending' ? (
            <Loader2 className="size-4 mt-1 opacity-50 animate-spin" />
          ) : state === 'notice' ? (
            <Info className="size-4 mt-1 opacity-50" />
          ) : (
            <CheckCircle2 className="size-4 mt-1 opacity-50" />
          )}
          <div className="stack">
            <h3 className="font-medium tracking-tight">{title}</h3>
            {children}
            <p className="text-sm [&_p]:leading-relaxed">{description}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function bannerState(isPending: boolean, isTimedOut: boolean): BannerState {
  return isPending ? 'pending' : isTimedOut ? 'notice' : 'success';
}
